import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { 
  FileText,
  Download,
  Filter,
  Loader2,
  Clock,
  Calendar,
  Users,
  TrendingUp,
  ChevronDown,
  ChevronRight,
  Edit,
  Plus,
  Trash2,
  X,
  Save
} from "lucide-react";
import { toast } from "sonner";
import { format, subDays, startOfWeek, endOfWeek, startOfMonth, endOfMonth } from "date-fns";
import { fmtTz, isoToBusinessInput, businessInputToIso } from "../lib/time";

export default function AdminRecords() {
  const { authAxios } = useAuth();
  const [records, setRecords] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reportLoading, setReportLoading] = useState(false);
  const [startDate, setStartDate] = useState(fmtTz(subDays(new Date(), 7), 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState(fmtTz(new Date(), 'yyyy-MM-dd'));
  const [selectedEmployee, setSelectedEmployee] = useState('all');
  const [expandedRows, setExpandedRows] = useState({});
  const [projects, setProjects] = useState([]);
  const [editingEntry, setEditingEntry] = useState(null);  // record being edited
  const [editSessions, setEditSessions] = useState([]);   // working copy
  const [savingEdit, setSavingEdit] = useState(false);
  const [addingEntryFor, setAddingEntryFor] = useState(false);  // showing the "add new day entry" dialog
  const [newEntry, setNewEntry] = useState({ user_id: '', date: fmtTz(new Date(), 'yyyy-MM-dd') });

  const fetchEmployees = useCallback(async () => {
    try {
      const res = await authAxios.get('/employees');
      setEmployees(res.data.filter(e => e.role === 'employee'));
    } catch (error) {
      console.error('Failed to fetch employees:', error);
    }
  }, [authAxios]);

  const fetchProjects = useCallback(async () => {
    try {
      const res = await authAxios.get('/projects?active_only=true');
      setProjects(res.data);
    } catch (error) {
      console.error('Failed to fetch projects:', error);
    }
  }, [authAxios]);

  const fetchRecords = useCallback(async () => {
    setLoading(true);
    try {
      const params = { start_date: startDate, end_date: endDate };
      if (selectedEmployee !== 'all') {
        params.employee_id = selectedEmployee;
      }
      const res = await authAxios.get('/admin/time-entries', { params });
      setRecords(res.data);
    } catch (error) {
      console.error('Failed to fetch records:', error);
    } finally {
      setLoading(false);
    }
  }, [authAxios, startDate, endDate, selectedEmployee]);

  const fetchReport = useCallback(async () => {
    setReportLoading(true);
    try {
      const params = { start_date: startDate, end_date: endDate };
      if (selectedEmployee !== 'all') {
        params.employee_id = selectedEmployee;
      }
      const res = await authAxios.get('/admin/reports/summary', { params });
      setReportData(res.data);
    } catch (error) {
      console.error('Failed to fetch report:', error);
    } finally {
      setReportLoading(false);
    }
  }, [authAxios, startDate, endDate, selectedEmployee]);

  useEffect(() => {
    fetchEmployees();
    fetchProjects();
  }, [fetchEmployees, fetchProjects]);

  useEffect(() => {
    fetchRecords();
    fetchReport();
  }, [fetchRecords, fetchReport]);

  const handleExportCSV = async () => {
    try {
      const params = new URLSearchParams({ start_date: startDate, end_date: endDate });
      if (selectedEmployee !== 'all') {
        params.append('employee_id', selectedEmployee);
      }
      
      const response = await authAxios.get(`/admin/reports/export-csv?${params.toString()}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `time_report_${startDate}_to_${endDate}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('CSV exported successfully!');
    } catch (error) {
      toast.error('Failed to export CSV');
    }
  };

  const handleForceClockOut = async (entryId, userName) => {
    if (!window.confirm(`Force clock-out for ${userName}? This will close their open shift right now.`)) return;
    try {
      await authAxios.post(`/admin/time-entries/${entryId}/force-clock-out`);
      toast.success(`Closed ${userName}'s shift`);
      fetchRecords();
      fetchReport();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to force clock-out');
    }
  };

  // datetime-local <-> ISO helpers
  const isoToLocal = isoToBusinessInput;
  const localToIso = businessInputToIso;

  const openEditDialog = (record) => {
    setEditingEntry(record);
    setEditSessions((record.sessions || []).map(s => ({
      ...s,
      __clock_in_local: isoToLocal(s.clock_in),
      __clock_out_local: isoToLocal(s.clock_out),
      __status: 'existing',  // existing | new | delete
    })));
  };

  const addSessionRow = () => {
    setEditSessions(prev => [...prev, {
      id: `__new_${Date.now()}_${Math.random().toString(36).slice(2,7)}`,
      project_id: projects[0]?.id || null,
      project_name: projects[0]?.name || '',
      __clock_in_local: '',
      __clock_out_local: '',
      __status: 'new',
    }]);
  };
  const markSessionDeleted = (idx) => {
    setEditSessions(prev => prev.map((s, i) => i === idx ? { ...s, __status: s.__status === 'new' ? 'discarded' : 'delete' } : s));
  };
  const updateSessionField = (idx, field, value) => {
    setEditSessions(prev => prev.map((s, i) => i === idx ? { ...s, [field]: value } : s));
  };

  const saveEdits = async () => {
    if (!editingEntry) return;
    setSavingEdit(true);
    try {
      // Process: deletes first, then edits, then adds
      for (const s of editSessions) {
        if (s.__status === 'delete') {
          await authAxios.delete(`/admin/time-entries/${editingEntry.id}/sessions/${s.id}`);
        }
      }
      for (const s of editSessions) {
        if (s.__status === 'existing') {
          await authAxios.put(`/admin/time-entries/${editingEntry.id}/sessions/${s.id}`, {
            clock_in: localToIso(s.__clock_in_local),
            clock_out: s.__clock_out_local ? localToIso(s.__clock_out_local) : '',
            project_id: s.project_id || null,
          });
        }
      }
      for (const s of editSessions) {
        if (s.__status === 'new') {
          if (!s.__clock_in_local) continue;
          await authAxios.post(`/admin/time-entries/${editingEntry.id}/sessions`, {
            clock_in: localToIso(s.__clock_in_local),
            clock_out: s.__clock_out_local ? localToIso(s.__clock_out_local) : null,
            project_id: s.project_id || null,
          });
        }
      }
      toast.success('Hours updated');
      setEditingEntry(null);
      fetchRecords();
      fetchReport();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save changes');
    } finally {
      setSavingEdit(false);
    }
  };

  const handleDeleteEntry = async () => {
    if (!editingEntry) return;
    if (!window.confirm(`Delete the ENTIRE day entry (${editingEntry.date}) for ${editingEntry.user_name}? This cannot be undone.`)) return;
    setSavingEdit(true);
    try {
      await authAxios.delete(`/admin/time-entries/${editingEntry.id}`);
      toast.success('Day entry deleted');
      setEditingEntry(null);
      fetchRecords();
      fetchReport();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    } finally {
      setSavingEdit(false);
    }
  };

  const createNewEntry = async () => {
    if (!newEntry.user_id || !newEntry.date) {
      toast.error('Pick an employee and date');
      return;
    }
    setSavingEdit(true);
    try {
      const res = await authAxios.post('/admin/time-entries', {
        user_id: newEntry.user_id,
        date: newEntry.date,
        sessions: [],
      });
      toast.success('Entry created — now add sessions');
      setAddingEntryFor(false);
      setNewEntry({ user_id: '', date: fmtTz(new Date(), 'yyyy-MM-dd') });
      await fetchRecords();
      openEditDialog(res.data);  // Immediately open the new entry for editing
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create entry');
    } finally {
      setSavingEdit(false);
    }
  };

  const formatMinutes = (minutes) => {
    const hrs = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hrs}h ${mins}m`;
  };

  const formatTime = (isoString) => {
    if (!isoString) return '--:--';
    return fmtTz(isoString, 'HH:mm');
  };

  const setDateRange = (range) => {
    const today = new Date();
    switch (range) {
      case 'week':
        setStartDate(format(startOfWeek(today, { weekStartsOn: 1 }), 'yyyy-MM-dd'));
        setEndDate(format(endOfWeek(today, { weekStartsOn: 1 }), 'yyyy-MM-dd'));
        break;
      case 'month':
        setStartDate(format(startOfMonth(today), 'yyyy-MM-dd'));
        setEndDate(format(endOfMonth(today), 'yyyy-MM-dd'));
        break;
      case 'last7':
        setStartDate(fmtTz(subDays(today, 7), 'yyyy-MM-dd'));
        setEndDate(format(today, 'yyyy-MM-dd'));
        break;
      case 'last30':
        setStartDate(fmtTz(subDays(today, 30), 'yyyy-MM-dd'));
        setEndDate(format(today, 'yyyy-MM-dd'));
        break;
      default:
        break;
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="admin-records">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-stone-900 font-heading">Time Records & Reports</h1>
          <p className="text-stone-500 mt-1">View all employee time data and generate reports</p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={() => setAddingEntryFor(true)}
            variant="outline"
            className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
            data-testid="add-entry-btn"
          >
            <Plus className="w-5 h-5 mr-2" />
            Add Entry
          </Button>
          <Button 
            onClick={handleExportCSV}
            className="bg-emerald-600 hover:bg-emerald-500 text-white shadow-lg shadow-emerald-600/20"
            data-testid="export-csv-btn"
          >
            <Download className="w-5 h-5 mr-2" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="border-0 shadow-lg shadow-stone-200/50 bg-white mb-6">
        <CardContent className="p-6">
          <div className="flex flex-col lg:flex-row gap-4 items-end">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 flex-1">
              <div className="space-y-2">
                <Label className="text-stone-600">Start Date</Label>
                <Input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  data-testid="records-start-date"
                  className="bg-stone-50 border-stone-200"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-stone-600">End Date</Label>
                <Input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  data-testid="records-end-date"
                  className="bg-stone-50 border-stone-200"
                />
              </div>
              <div className="space-y-2 col-span-2 sm:col-span-1">
                <Label className="text-stone-600">Employee</Label>
                <Select value={selectedEmployee} onValueChange={setSelectedEmployee}>
                  <SelectTrigger data-testid="employee-filter" className="bg-stone-50 border-stone-200">
                    <SelectValue placeholder="All Employees" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Employees</SelectItem>
                    {employees.map(emp => (
                      <SelectItem key={emp.id} value={emp.id}>{emp.full_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button variant="outline" size="sm" onClick={() => setDateRange('last7')} className="text-xs">
                Last 7 Days
              </Button>
              <Button variant="outline" size="sm" onClick={() => setDateRange('week')} className="text-xs">
                This Week
              </Button>
              <Button variant="outline" size="sm" onClick={() => setDateRange('month')} className="text-xs">
                This Month
              </Button>
              <Button variant="outline" size="sm" onClick={() => setDateRange('last30')} className="text-xs">
                Last 30 Days
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="records" className="space-y-6">
        <TabsList className="bg-stone-100 p-1 rounded-xl">
          <TabsTrigger value="records" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow">
            <FileText className="w-4 h-4 mr-2" />
            Time Entries
          </TabsTrigger>
          <TabsTrigger value="summary" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow">
            <TrendingUp className="w-4 h-4 mr-2" />
            Summary Report
          </TabsTrigger>
        </TabsList>

        <TabsContent value="records">
          <Card className="border-0 shadow-lg shadow-stone-200/50 bg-white">
            <CardHeader>
              <CardTitle className="text-lg font-heading flex items-center gap-2">
                <Filter className="w-5 h-5 text-stone-400" />
                Time Entries ({records.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
                </div>
              ) : records.length === 0 ? (
                <div className="text-center py-12 text-stone-500">
                  No records found for the selected filters
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-stone-100">
                        <TableHead className="w-8"></TableHead>
                        <TableHead className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Date</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Employee</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Sessions</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider text-stone-500 font-semibold">First In</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Last Out</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Work Time</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Break Time</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Status</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider text-stone-500 font-semibold text-right">Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {records.map((record) => {
                        const sessions = record.sessions || [];
                        const isOpenShift = sessions.some(s => s.clock_in && !s.clock_out);
                        const isOpen = !!expandedRows[record.id];
                        const sessionCount = sessions.length;
                        return (
                        <React.Fragment key={record.id}>
                        <TableRow
                          className="border-stone-100 hover:bg-stone-50 cursor-pointer"
                          onClick={() => setExpandedRows(r => ({ ...r, [record.id]: !isOpen }))}
                          data-testid={`record-row-${record.id}`}
                        >
                          <TableCell>{sessionCount > 0 ? (isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />) : null}</TableCell>
                          <TableCell className="font-medium">
                            {fmtTz(record.date, 'EEE, MMM d')}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <div className="w-7 h-7 bg-emerald-600 rounded-full flex items-center justify-center text-white font-bold text-xs">
                                {record.user_name?.split(' ').map(n => n[0]).join('').toUpperCase()}
                              </div>
                              <span>{record.user_name}</span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="font-mono">{sessionCount}</Badge>
                          </TableCell>
                          <TableCell className="font-mono text-sm">{formatTime(record.clock_in)}</TableCell>
                          <TableCell className="font-mono text-sm">{formatTime(record.clock_out)}</TableCell>
                          <TableCell className="font-mono text-sm text-emerald-600 font-medium">
                            {formatMinutes(record.total_work_minutes || 0)}
                          </TableCell>
                          <TableCell className="font-mono text-sm text-amber-600">
                            {formatMinutes(record.total_break_minutes || 0)}
                          </TableCell>
                          <TableCell>
                            <Badge 
                              variant="outline"
                              className={
                                record.status === 'working' ? 'bg-emerald-100 text-emerald-800 border-emerald-200' :
                                record.status === 'on_break' ? 'bg-amber-100 text-amber-800 border-amber-200' :
                                'bg-stone-100 text-stone-600 border-stone-200'
                              }
                            >
                              {record.status === 'working' ? 'Working' : record.status === 'on_break' ? 'On Break' : 'Off'}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                            <div className="flex justify-end gap-2">
                              {isOpenShift && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-rose-600 border-rose-300 hover:bg-rose-50"
                                  onClick={() => handleForceClockOut(record.id, record.user_name)}
                                  data-testid={`force-clock-out-btn-${record.id}`}
                                >
                                  Force Clock-Out
                                </Button>
                              )}
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-emerald-700 border-emerald-300 hover:bg-emerald-50"
                                onClick={() => openEditDialog(record)}
                                data-testid={`edit-hours-btn-${record.id}`}
                              >
                                <Edit className="w-3.5 h-3.5 mr-1.5" />
                                Edit Hours
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                        {isOpen && sessions.length > 0 && (
                          <TableRow className="bg-stone-50/60">
                            <TableCell></TableCell>
                            <TableCell colSpan={9} className="py-3">
                              <div className="text-xs uppercase tracking-wider text-stone-500 mb-2">Sessions</div>
                              <table className="w-full text-sm" data-testid={`sessions-${record.id}`}>
                                <thead>
                                  <tr className="text-left text-stone-500">
                                    <th className="py-1 pr-4">#</th>
                                    <th className="py-1 pr-4">Project</th>
                                    <th className="py-1 pr-4">Clock In</th>
                                    <th className="py-1 pr-4">Clock Out</th>
                                    <th className="py-1 pr-4 text-right">Work</th>
                                    <th className="py-1 pr-4">Employee check</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {sessions.map((sess, i) => (
                                    <tr key={sess.id || i} className="border-t border-stone-200">
                                      <td className="py-1.5 pr-4 font-mono">{i + 1}</td>
                                      <td className="py-1.5 pr-4">
                                        <Badge variant="outline" className="border-emerald-300 text-emerald-800">
                                          {sess.project_name || 'No project'}
                                        </Badge>
                                      </td>
                                      <td className="py-1.5 pr-4 font-mono">{formatTime(sess.clock_in)}</td>
                                      <td className="py-1.5 pr-4 font-mono">{sess.clock_out ? formatTime(sess.clock_out) : <span className="text-emerald-600">open</span>}</td>
                                      <td className="py-1.5 pr-4 text-right font-mono text-emerald-700">{formatMinutes(sess.session_minutes || 0)}</td>
                                      <td className="py-1.5 pr-4" data-testid={`session-check-${sess.id}`}>
                                        {sess.time_confirmed === true && <Badge className="bg-emerald-100 text-emerald-700">Confirmed</Badge>}
                                        {sess.time_confirmed === false && (
                                          <span className="text-amber-700 text-xs">
                                            <Badge className={sess.time_issue_resolved ? 'bg-stone-100 text-stone-600' : 'bg-amber-100 text-amber-800'}>
                                              {sess.time_issue_resolved ? 'Fixed' : 'Says wrong'}
                                            </Badge>
                                            <span className="ml-2 italic">"{sess.time_issue_note}"</span>
                                          </span>
                                        )}
                                        {sess.time_confirmed == null && sess.clock_out && <span className="text-stone-400 text-xs">No answer</span>}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </TableCell>
                          </TableRow>
                        )}
                        </React.Fragment>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="summary">
          <Card className="border-0 shadow-lg shadow-stone-200/50 bg-white">
            <CardHeader>
              <CardTitle className="text-lg font-heading flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-stone-400" />
                Summary Report
              </CardTitle>
            </CardHeader>
            <CardContent>
              {reportLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
                </div>
              ) : !reportData?.summaries?.length ? (
                <div className="text-center py-12 text-stone-500">
                  No data for the selected period
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-stone-100">
                        <TableHead className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Employee</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Department</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Days Worked</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Total Work</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Total Breaks</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Avg/Day</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {reportData.summaries.map((summary) => (
                        <TableRow key={summary.user_id} className="border-stone-100 hover:bg-stone-50">
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <div className="w-7 h-7 bg-emerald-600 rounded-full flex items-center justify-center text-white font-bold text-xs">
                                {summary.user_name?.split(' ').map(n => n[0]).join('').toUpperCase()}
                              </div>
                              <span className="font-medium">{summary.user_name}</span>
                            </div>
                          </TableCell>
                          <TableCell className="text-stone-600">{summary.department || '-'}</TableCell>
                          <TableCell className="font-mono">{summary.days_worked}</TableCell>
                          <TableCell className="font-mono text-emerald-600 font-medium">
                            {formatMinutes(summary.total_work_minutes)}
                          </TableCell>
                          <TableCell className="font-mono text-amber-600">
                            {formatMinutes(summary.total_break_minutes)}
                          </TableCell>
                          <TableCell className="font-mono text-stone-600">
                            {summary.days_worked > 0 
                              ? formatMinutes(Math.round(summary.total_work_minutes / summary.days_worked))
                              : '-'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Edit Hours Dialog */}
      <Dialog open={!!editingEntry} onOpenChange={(open) => !open && setEditingEntry(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="edit-hours-dialog">
          <DialogHeader>
            <DialogTitle>Edit Hours — {editingEntry?.user_name}</DialogTitle>
            <DialogDescription>
              {editingEntry && fmtTz(editingEntry.date, 'EEEE, MMMM d, yyyy')} · Adjust clock-in/out times, change project, add or delete sessions. Times round to the nearest 15 minutes.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            {editSessions.filter(s => s.__status !== 'discarded' && s.__status !== 'delete').length === 0 && (
              <p className="text-center text-sm text-stone-500 py-6">No sessions yet. Click "Add Session" below.</p>
            )}
            {editSessions.map((s, idx) => {
              if (s.__status === 'discarded' || s.__status === 'delete') return null;
              return (
                <div key={s.id || idx} className="border rounded-lg p-4 bg-stone-50/50" data-testid={`edit-session-row-${idx}`}>
                  <div className="flex items-start justify-between mb-2">
                    <span className="text-xs font-mono uppercase tracking-wider text-stone-500">Session {idx + 1}</span>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-rose-600 hover:bg-rose-50 h-7 px-2"
                      onClick={() => markSessionDeleted(idx)}
                      data-testid={`delete-session-btn-${idx}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div>
                      <Label className="text-xs">Clock In</Label>
                      <Input
                        type="datetime-local"
                        step="900"
                        value={s.__clock_in_local || ''}
                        onChange={(e) => updateSessionField(idx, '__clock_in_local', e.target.value)}
                        data-testid={`edit-session-in-${idx}`}
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Clock Out</Label>
                      <Input
                        type="datetime-local"
                        step="900"
                        value={s.__clock_out_local || ''}
                        onChange={(e) => updateSessionField(idx, '__clock_out_local', e.target.value)}
                        placeholder="Leave blank for open"
                        data-testid={`edit-session-out-${idx}`}
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Project</Label>
                      <Select
                        value={s.project_id || ''}
                        onValueChange={(v) => updateSessionField(idx, 'project_id', v)}
                      >
                        <SelectTrigger data-testid={`edit-session-project-${idx}`}><SelectValue placeholder="None" /></SelectTrigger>
                        <SelectContent>
                          {projects.map(p => (
                            <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              );
            })}
            <Button
              variant="outline"
              onClick={addSessionRow}
              className="w-full border-dashed border-stone-300"
              data-testid="add-session-btn"
            >
              <Plus className="w-4 h-4 mr-2" /> Add Session
            </Button>
          </div>

          <DialogFooter className="gap-2 sm:gap-2 flex-col sm:flex-row">
            <Button variant="ghost" className="text-rose-600 hover:bg-rose-50 sm:mr-auto" onClick={handleDeleteEntry} disabled={savingEdit} data-testid="delete-entry-btn">
              <Trash2 className="w-4 h-4 mr-2" /> Delete Entire Day
            </Button>
            <Button variant="outline" onClick={() => setEditingEntry(null)} disabled={savingEdit}>
              <X className="w-4 h-4 mr-2" /> Cancel
            </Button>
            <Button onClick={saveEdits} disabled={savingEdit} className="bg-emerald-600 hover:bg-emerald-700" data-testid="save-hours-btn">
              {savingEdit ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Entry Dialog (for forgotten days) */}
      <Dialog open={addingEntryFor} onOpenChange={setAddingEntryFor}>
        <DialogContent className="max-w-md" data-testid="add-entry-dialog">
          <DialogHeader>
            <DialogTitle>Add Time Entry</DialogTitle>
            <DialogDescription>Manually create a day entry for an employee who forgot to clock in entirely.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Employee</Label>
              <Select value={newEntry.user_id} onValueChange={(v) => setNewEntry(n => ({ ...n, user_id: v }))}>
                <SelectTrigger data-testid="new-entry-employee"><SelectValue placeholder="Choose employee" /></SelectTrigger>
                <SelectContent>
                  {employees.map(e => <SelectItem key={e.id} value={e.id}>{e.full_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Date</Label>
              <Input type="date" value={newEntry.date} onChange={(e) => setNewEntry(n => ({ ...n, date: e.target.value }))} data-testid="new-entry-date" />
            </div>
            <p className="text-xs text-stone-500">After creating, you'll be taken straight to the Edit Hours view to add sessions.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddingEntryFor(false)} disabled={savingEdit}>Cancel</Button>
            <Button onClick={createNewEntry} disabled={savingEdit} className="bg-emerald-600 hover:bg-emerald-700" data-testid="create-entry-btn">
              {savingEdit ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
              Create &amp; Edit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
